/**
 * Named feed gaps (AJ, 2026-09-02: "if we don't [have the numbers] then we
 * need a way to indicate that we need data to provide the insight/kpi").
 *
 * Today a widget with no data renders one of three states, none of which tells
 * an owner what to do about it:
 *
 *   "Not available - awaiting source data"   the honest gate, but it names
 *     nothing. Every Best Disposal Safety and Fleet tile reads this, because
 *     that tenant has no asset rows at all and MER-589 routes the pin failure
 *     here.
 *   "Limited activity this period. Try a wider range."   sends an owner off to
 *     widen a range that will never fill, when the column is empty for every
 *     range.
 *   "Pending. Source data not yet available."   the blocked-widget caption.
 *
 * Simon's demo review called this out on the fleet tiles specifically:
 * asset.utilization_rate_4wk, asset.idle_hours and asset.annual_maintenance_cost
 * are columns with ZERO non-null rows in production. They are not sparse and
 * they are not broken. They are empty because nobody has connected the feed
 * that fills them, and he asked that the dashboard say so rather than force
 * them into the demo looking blank.
 *
 * This module is the naming half. A column earns an entry ONLY when it comes
 * from exactly one feed, so the sentence we put on the tile is provable from
 * the pipeline rather than guessed:
 *
 *   telematics safety feed
 *     normalize_samsara_to_assets is the only writer of vehicle_name,
 *     safety_score, the harsh-event counts, crash / speeding counts and drive
 *     time. No safety export, no ASSET rows at all, which is exactly Best
 *     Disposal's position.
 *
 *   telematics engine-hours feed
 *     normalize.py writes utilization_rate_4wk: None on BOTH asset paths.
 *     engine_hours / idle_hours / productive_hours are the v4.4 "Engine
 *     utilization" block that only the telematics API supplies; since MER-669
 *     the Samsara connector writes them, so they fill for a tenant with that
 *     API and are named only when the tenant has no source data. The weekly
 *     safety export carries drive time, not engine hours.
 *
 *   maintenance work-order feed
 *     normalize_fleetio_to_assets is the only writer of annual_maintenance_cost,
 *     last_maintenance_date and maintenance_interval_days. That feed is
 *     shelved, so the telematics path writes None.
 *
 * Deliberately NOT a blanket rule over every empty column. sanityGate's
 * NO_SOURCE_DATA note draws the line and it is the right one: an unexplained
 * empty column may be a pipeline defect, and dressing a defect up as an absent
 * feed hides it. An unregistered column keeps exactly the behaviour it has
 * today.
 *
 * Feed names are vendor-neutral on purpose. The platform is source-agnostic by
 * contract (source-specific code lives in connectors/), tenants run different
 * telematics, and a tile telling Best Disposal to connect a vendor they do not
 * use would be worse than the empty tile it replaced. "The telematics safety
 * feed" is true for every tenant and is still the exact thing an owner has to
 * go and switch on.
 *
 * Plain .mjs (with a .d.mts beside it) so the node:test suite imports the real
 * decision function rather than replaying it from source.
 */

/**
 * Shared opener with sanityGate's GATE_COPY ("Not available - awaiting source
 * data"). These render in the same muted slot and must read as one family: the
 * generic gate when we cannot say what is missing, the named feed when we can.
 * missing-feed.test.mjs pins the two together.
 */
export const AWAITING_PREFIX = "Not available - awaiting";

/**
 * One entry per feed.
 *
 * `source` scopes the entry to the entity that feed populates, so a column name
 * that also exists elsewhere cannot pick up a gap it does not have.
 *
 * `alwaysMissing` is the important distinction and it decides how loudly we may
 * speak:
 *
 *   true  - the pipeline CANNOT populate this column on any path we run today,
 *           so an empty result is definitionally the feed gap and we say so
 *           even for a merely empty range.
 *   false - the column is populated for tenants that have the feed (DWS reads a
 *           real safety score every week). An empty RANGE on such a column is
 *           just a sparse range, so we stay quiet and only name the feed when
 *           the tenant has no source data at all.
 *
 * Patterns are anchored so a lookalike (asset_utilization_target,
 * idle_hours_goal) does not inherit its neighbour's gap.
 *
 * Remove a column here the moment its feed lands, the same discipline
 * FORCE_REVIEW_COLUMNS carries: a stale entry would keep saying "awaiting"
 * over a number that had arrived.
 */
const FEED_GAPS = [
  {
    feed: "the telematics safety feed",
    source: /asset/i,
    alwaysMissing: false,
    columns: [
      /^vehicle_name$/,
      /^safety_score$/,
      /^total_events$/,
      /^total_behaviors$/,
      /^harsh_(accel|brake|turn)_count$/,
      /^crash_count$/,
      /^speeding_count$/,
      /^drive_time_hours$/,
    ],
  },
  {
    feed: "the telematics engine-hours feed",
    source: /asset/i,
    alwaysMissing: true,
    columns: [
      // The manifest binds asset_utilization_rate; the warehouse column is
      // utilization_rate_4wk. Both spellings resolve to the same empty column.
      // Still written None on every path: utilization needs available hours
      // as well as engine hours, and nothing supplies the former.
      /^asset_utilization_rate$/,
      /^utilization_rate(_4wk)?$/,
    ],
  },
  {
    // MER-669: the Samsara connector now writes these from the ECU engine
    // counters, so they fill for any tenant with the telematics API and are
    // no longer always-missing. Same feed name, scoped the way the safety
    // columns are: named only when the tenant has no source data at all.
    feed: "the telematics engine-hours feed",
    source: /asset/i,
    alwaysMissing: false,
    columns: [/^idle_hours$/, /^engine_hours$/, /^productive_hours$/],
  },
  {
    feed: "the maintenance work-order feed",
    source: /asset/i,
    alwaysMissing: true,
    columns: [
      /^annual_maintenance_cost$/,
      /^last_maintenance_date$/,
      /^maintenance_interval_days$/,
    ],
  },
  {
    // MER-840. days_worked is the denominator of every per-day board line and
    // it is SUPPLIED by the client, in their own operating calendar, with no
    // derived fallback anywhere in the pipeline: a day count built from our
    // own service events is our own output, so a rate divided by it would be
    // graded against our own arithmetic (MER-716), and it is measurably wrong.
    //
    // So an empty column here is not a bug and not a sparse range. It is a
    // file nobody has uploaded yet, which is a thing an owner can actually go
    // and do something about. The generic gate copy would send them to look
    // at the pipeline instead, which is exactly the wrong place.
    //
    // alwaysMissing is FALSE, not true: unlike the telematics feeds above,
    // this column fills for any tenant who uploads a calendar, so an empty
    // range on a tenant who has one is a sparse range and not a feed gap.
    feed: "the client operating calendar",
    source: /kpi_summary/i,
    alwaysMissing: false,
    columns: [/^days_worked$/],
  },
];

function matchesSource(gap, source) {
  // An entry that declares a source only answers for that source. A widget
  // whose source we could not resolve never inherits a scoped gap.
  if (!gap.source) return true;
  return typeof source === "string" && gap.source.test(source);
}

/**
 * The feed that would fill this column, or null when we cannot name one.
 * Returns the feed, the tile-ready copy, and whether the column is one the
 * pipeline can never fill, so a caller does not re-assemble any of it.
 */
export function missingFeedForColumn(column, source) {
  if (!column) return null;
  const c = String(column).toLowerCase().trim();
  if (!c) return null;
  for (const gap of FEED_GAPS) {
    if (!matchesSource(gap, source)) continue;
    if (gap.columns.some((re) => re.test(c))) {
      return {
        feed: gap.feed,
        copy: `${AWAITING_PREFIX} ${gap.feed}`,
        alwaysMissing: Boolean(gap.alwaysMissing),
      };
    }
  }
  return null;
}

/**
 * First column in `columns` that names a feed. Charts and tables bind their
 * measures in a series or a column allow-list rather than a single `field`, so
 * the caller hands over every column the widget touches.
 */
export function missingFeedForColumns(columns, source) {
  for (const column of columns ?? []) {
    const gap = missingFeedForColumn(column, source);
    if (gap) return gap;
  }
  return null;
}

/**
 * MER-717 / MER-705: a feed the manifest names for a widget that has no column
 * to bind yet.
 *
 * FEED_GAPS above answers for a COLUMN the pipeline already writes. The board
 * catalogue also carries metrics no column can hold until a client sends a feed
 * we do not ingest at all (a general ledger export, a payroll register, a fleet
 * register), so there is nothing to register by column. Those tiles declare the
 * feed in the template (`awaiting_feed`), and this turns the declaration into
 * the same tile copy. It answers only for a blocked or suppressed widget; the
 * render decision below enforces that, so a widget whose feed has arrived and
 * gone live can never keep saying it is waiting.
 *
 * The name must be plain ASCII, vendor neutral and short enough that the whole
 * sentence fits a tile (DECLARED_COPY_MAX, the limit the registered copy is
 * held to). Anything else is refused rather than rendered.
 */
export const DECLARED_COPY_MAX = 70;

export function declaredFeedGap(feed) {
  if (typeof feed !== "string") return null;
  const name = feed.trim();
  if (!name || /[^\x20-\x7e]/.test(name)) return null;
  const copy = `${AWAITING_PREFIX} ${name}`;
  if (copy.length > DECLARED_COPY_MAX) return null;
  return { feed: name, copy, alwaysMissing: true };
}

/**
 * The render decision, kept out of the component so it is directly testable.
 *
 * Returns the feed gap to render in place of the value, or null to leave the
 * widget's existing state alone. Three entry conditions, in the order they can
 * be known:
 *
 *   status blocked / suppressed -- the manifest already says this widget waits
 *     on a source. Naming the source is strictly more useful than "Pending".
 *     `deferred` is excluded: that widget is out of scope for the build phase,
 *     not starved of a feed, and claiming a feed gap there would be a lie.
 *
 *   noSourceData -- the query could not resolve because the tenant has no rows
 *     or no such column (MER-446 / MER-589). That is a feed gap for any
 *     registered column, whether or not other tenants have the feed.
 *
 *   isEmpty -- the query resolved and returned nothing. Only an
 *     `alwaysMissing` column may be named here: for every other column an
 *     empty result is a sparse range, and telling an owner to connect a feed
 *     they already have would be worse than the empty tile.
 *
 * A registered column always wins. Only when no column names a feed does a
 * declared `awaitingFeed` (declaredFeedGap) answer, and only for a blocked or
 * suppressed widget.
 *
 * A registered column that DOES return a value is never gated. A feed that has
 * started arriving must show its number.
 */
export function resolveFeedGap(opts = {}) {
  const {
    column,
    columns,
    source,
    status,
    isEmpty = false,
    noSourceData = false,
    awaitingFeed,
  } = opts;
  const gap = column
    ? missingFeedForColumn(column, source)
    : missingFeedForColumns(columns, source);
  if (!gap) {
    const declared = declaredFeedGap(awaitingFeed);
    return declared && (status === "blocked" || status === "suppressed") ? declared : null;
  }
  if (status && status !== "ok") {
    return status === "blocked" || status === "suppressed" ? gap : null;
  }
  if (noSourceData) return gap;
  return isEmpty && gap.alwaysMissing ? gap : null;
}
